// US-04: Single shared subscription to users/{uid}/entitlements/summary — hint only; API is authoritative.
import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

const EntitlementSummaryContext = createContext(null);

function useEntitlementSummarySubscription() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshotExists, setSnapshotExists] = useState(false);

  useEffect(() => {
    let unsubFirestore = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubFirestore) {
        unsubFirestore();
        unsubFirestore = null;
      }

      if (!user) {
        setSummary(null);
        setLoading(false);
        setError(null);
        setSnapshotExists(false);
        return;
      }

      setLoading(true);
      setError(null);

      const ref = doc(db, 'users', user.uid, 'entitlements', 'summary');
      unsubFirestore = onSnapshot(
        ref,
        (snap) => {
          setSnapshotExists(snap.exists());
          setSummary(snap.exists() ? snap.data() : null);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('[EntitlementSummary]', err);
          setError(err);
          setSummary(null);
          setSnapshotExists(false);
          setLoading(false);
        }
      );
    });

    return () => {
      if (unsubFirestore) unsubFirestore();
      unsubAuth();
    };
  }, []);

  return { summary, loading, error, snapshotExists };
}

export function EntitlementSummaryProvider({ children }) {
  const value = useEntitlementSummarySubscription();
  return (
    <EntitlementSummaryContext.Provider value={value}>
      {children}
    </EntitlementSummaryContext.Provider>
  );
}

/**
 * @returns {{
 *   summary: object | null,
 *   loading: boolean,
 *   error: Error | null,
 *   snapshotExists: boolean
 * }}
 */
export function useEntitlementSummary() {
  const ctx = useContext(EntitlementSummaryContext);
  if (ctx === null) {
    throw new Error('useEntitlementSummary must be used within EntitlementSummaryProvider');
  }
  return ctx;
}
